import { useMemo } from 'react';
import type { HouseConfig } from '../types';
import { getResultsFocusTurn } from '../core/rules/resultTurns';
import { resolvePrecedingTurn } from '../core/rules/cutoffState';
import { platformDayKeyForAcceptanceTurn } from '../core/rules/windowSchedule';
import {
  findYoutubeVideoIdForDraw,
  youtubeWatchUrl,
} from '../core/lottery/youtubeDrawLink';
import { useServerClock } from './useServerClock';
import { useRecentPlatformLotteryDraws } from './useRecentPlatformLotteryDraws';

export type FocusDrawYoutube = {
  /** Focus/preceding watch URL for overview CTAs. */
  url: string | null;
  /** Platform/publish dayKey the focus `url` belongs to. */
  dayKey: string | null;
  window: 'day' | 'night' | null;
  /** Per-turn lookup from the live platform draw feed (no focus fallback). */
  urlForTurn: (dayKey: string, window: 'day' | 'night') => string | null;
  idForTurn: (dayKey: string, window: 'day' | 'night') => string | null;
};

function emptyFocus(
  urlForTurn: FocusDrawYoutube['urlForTurn'],
  idForTurn: FocusDrawYoutube['idForTurn'],
): FocusDrawYoutube {
  return { url: null, dayKey: null, window: null, urlForTurn, idForTurn };
}

/**
 * Watch URL for the relevant platform draw: prefer the focus turn's video,
 * else the preceding turn (covers next-window acceptance while prior draw is up).
 * Preceding acceptance dayKeys are mapped to platform/publish dayKeys for lookup.
 * Use `urlForTurn` on result cards so each published turn gets its own video.
 */
export function useFocusDrawYoutubeUrl(
  config: HouseConfig | null | undefined,
): FocusDrawYoutube {
  const { now } = useServerClock();
  const source = String(config?.source ?? 'florida');
  const { draws } = useRecentPlatformLotteryDraws(
    source === 'florida' || source === 'newyork' ? source : null,
    24,
  );
  return useMemo(() => {
    const idForTurn = (dayKey: string, window: 'day' | 'night') => (
      findYoutubeVideoIdForDraw(draws, dayKey, window)
    );
    const urlForTurn = (dayKey: string, window: 'day' | 'night') => {
      const id = idForTurn(dayKey, window);
      return id ? youtubeWatchUrl(id) : null;
    };
    if (source !== 'florida' && source !== 'newyork') {
      return emptyFocus(urlForTurn, idForTurn);
    }
    const focus = getResultsFocusTurn(config, now);
    const preceding = resolvePrecedingTurn(config, now);
    const focusId = focus
      ? findYoutubeVideoIdForDraw(draws, focus.dayKey, focus.window)
      : null;
    if (focusId && focus) {
      return {
        url: youtubeWatchUrl(focusId),
        dayKey: focus.dayKey,
        window: focus.window,
        urlForTurn,
        idForTurn,
      };
    }
    if (!preceding) return emptyFocus(urlForTurn, idForTurn);
    const platformDayKey = platformDayKeyForAcceptanceTurn(
      config,
      preceding.window,
      preceding.dayKey,
    );
    const precedingId = findYoutubeVideoIdForDraw(
      draws,
      platformDayKey,
      preceding.window,
    );
    if (!precedingId) return emptyFocus(urlForTurn, idForTurn);
    return {
      url: youtubeWatchUrl(precedingId),
      dayKey: platformDayKey,
      window: preceding.window,
      urlForTurn,
      idForTurn,
    };
  }, [config, now, draws, source]);
}
